import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import ErrorBoundary from './ErrorBoundary';
import { ToastProvider } from './ui/toast';
import { SessionProvider } from './session';
import { applyTheme, readTheme } from './ui/theme';
import './styles.css';
import { reportClientError } from './ui/report';

/* Before anything renders. A theme applied after React mounts means every load
   paints the default palette and then repaints — the same class of defect as
   the layout flash that was fixed in `bad9835`, and just as visible. */
applyTheme(readTheme());

/* One client for the whole app.
 *
 * The defaults matter more than they look. This app's data is a *plan* — it
 * changes when the user acts, when a calendar syncs, and when the websocket
 * says so, not on a timer. Polling would put load on a single small
 * container to discover nothing had changed, so refetching is driven by explicit
 * invalidation instead.
 *
 * `retry: false` because the API answers `{ ok: false }` with a reason rather
 * than throwing; a retry would repeat a request the server already declined,
 * three times, before showing the same sentence. */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000
    }
  }
});

/* Errors React never sees — a failed promise in a click handler, a throw in a
   timer — reach the same list as render crashes. */
window.addEventListener('error', e => {
  if (e.error) reportClientError(e.error, { where: 'window' });
});
window.addEventListener('unhandledrejection', e => {
  reportClientError(e.reason, { where: 'promise' });
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </SessionProvider>
      </ToastProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
